import * as React from 'react';
import { cn } from '@/lib/utils';

/**
 * Format a raw digit string (no decimals) into PT-BR money format.
 * E.g. "12345" → "123,45", "1" → "0,01", "" → "0,00"
 */
function formatFromDigits(digits: string): string {
  if (!digits) return '0,00';

  // Pad to at least 3 digits so we always have 2 decimal places
  const padded = digits.padStart(3, '0');
  const integerPart = padded.slice(0, padded.length - 2);
  const decimalPart = padded.slice(padded.length - 2);

  // Remove leading zeros from integer part but keep at least one
  const cleanInteger = integerPart.replace(/^0+/, '') || '0';

  // Add thousand separators
  const withSeparators = cleanInteger.replace(/\B(?=(\d{3})+(?!\d))/g, '.');

  return `${withSeparators},${decimalPart}`;
}

/**
 * Extract raw digits from a PT-BR formatted money string.
 * E.g. "1.234,56" → "123456", "0,01" → "1", "12,45" → "1245"
 */
function toDigits(formatted: string): string {
  // Remove everything except digits
  const digits = formatted.replace(/\D/g, '');
  // Remove leading zeros
  return digits.replace(/^0+/, '') || '';
}

export interface CurrencyInputProps
  extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'value' | 'onChange'> {
  value: string;
  onChange: (value: string) => void;
  error?: string;
  label?: string;
}

const CurrencyInput = React.forwardRef<HTMLInputElement, CurrencyInputProps>(
  ({ className, value, onChange, disabled, placeholder, error, label, id, ...props }, ref) => {
    const generatedId = React.useId();
    const inputId = id || ((props as Record<string, unknown>).name as string) || generatedId;

    // Convert current value to display format
    const displayValue = React.useMemo(() => {
      if (!value && value !== '0,00') return '0,00';
      // Value comes in PT-BR format like "12,45" or "1.234,56"
      const digits = toDigits(value);
      return formatFromDigits(digits);
    }, [value]);

    const hasSelection = (input: HTMLInputElement) => {
      const start = input.selectionStart ?? 0;
      const end = input.selectionEnd ?? 0;
      return end - start > 0;
    };

    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
      // Allow: Tab, Enter, Escape, arrows for navigation
      if (['Tab', 'Enter', 'Escape', 'ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(e.key)) {
        return;
      }

      // Handle Backspace/Delete: clear all if text is selected, otherwise remove last digit
      if (e.key === 'Backspace' || e.key === 'Delete') {
        e.preventDefault();
        if (hasSelection(e.currentTarget)) {
          onChange('0,00');
          return;
        }
        const digits = toDigits(value);
        if (digits.length <= 1) {
          onChange('0,00');
          return;
        }
        const newDigits = digits.slice(0, -1);
        onChange(formatFromDigits(newDigits));
        return;
      }

      // Only allow digit keys
      if (/^\d$/.test(e.key)) {
        e.preventDefault();
        // If text is selected, replace with the new digit
        const baseDigits = hasSelection(e.currentTarget) ? '' : toDigits(value);
        const newDigits = baseDigits + e.key;
        onChange(formatFromDigits(newDigits));
        return;
      }

      // Allow Ctrl+A, Ctrl+C, Ctrl+V, Ctrl+X
      if (e.ctrlKey || e.metaKey) {
        return;
      }

      // Block all other keys
      e.preventDefault();
    };

    const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
      e.preventDefault();
      const pasted = e.clipboardData.getData('text');

      // Extract digits from pasted content
      const pastedDigits = pasted.replace(/\D/g, '');
      if (!pastedDigits) return;

      // Remove leading zeros and format
      const cleanDigits = pastedDigits.replace(/^0+/, '') || '';
      onChange(formatFromDigits(cleanDigits));
    };

    return (
      <div className="w-full">
        {label && (
          <label htmlFor={inputId} className="block text-sm font-medium text-gray-700 mb-2">
            {label}
          </label>
        )}
        <div className="relative">
          <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
            <span className="text-gray-500 sm:text-sm">R$</span>
          </div>
          <input
            id={inputId}
            ref={ref}
            type="text"
            inputMode="numeric"
            value={displayValue}
            onKeyDown={handleKeyDown}
            onPaste={handlePaste}
            onChange={() => {
              // Controlled via onKeyDown and onPaste only
            }}
            placeholder={placeholder ?? '0,00'}
            disabled={disabled}
            className={cn(
              'flex h-9 w-full rounded-md border border-gray-300 bg-transparent pl-10 pr-3 py-1 text-sm shadow-sm transition-colors file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-gray-400 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-blue-500 disabled:cursor-not-allowed disabled:opacity-50',
              error && 'border-red-500 focus-visible:ring-red-500',
              className,
            )}
            {...props}
          />
        </div>
        {error && <p className="mt-1 text-sm text-red-500">{error}</p>}
      </div>
    );
  },
);
CurrencyInput.displayName = 'CurrencyInput';

export { CurrencyInput };
